import { Injectable } from '@angular/core';
import { Job } from './job.model';
import { Observable } from 'rxjs';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { LoggerService } from '../shared/services/logger/logger.service';
import { List } from 'immutable';
import { IResponseInfo } from 'i2g-ng-utils';
import { map } from 'rxjs/operators';
import { JobPortalData } from './models/job-portal-data.model';
import { JobEstimate } from './models/submodels/job-estimate.model';
import { Attachments } from './models/submodels/attachments.model';
import { JobNote } from './models/submodels/job-note.model';

export const ACCEPT_VERSION = '1.0.0';

@Injectable()
export class JobDataService {
  baseUrl = '/business/jobs';

  protected headers: any = {
    [ACCEPT_VERSION_HEADER]: ACCEPT_VERSION,
  };

  constructor(
    private _loggerService: LoggerService,
    private _authHttpService: AuthHttpService
  ) {}

  fetchJobs(clientId: string): Observable<List<Job>> {
    this._loggerService.log('fetch job from network');

    const url = `${this.baseUrl}/clients/${clientId}`;

    return this._authHttpService.get(url, {}, this.headers).pipe(
      map(({ body }: IResponseInfo) => {
        return List<Job>((body['jobs'] || []).map((job) => new Job(job)));
      })
    );
  }

  fetchJobPortalData(jobId: string): Observable<JobPortalData> {
    this._loggerService.log('fetch job portal data from network');

    const url = `${this.baseUrl}/${jobId}/portal_data`;

    return this._authHttpService.get(url, {}, this.headers).pipe(
      map(({ body }: IResponseInfo) => {
        return new JobPortalData(body.portal_data);
      })
    );
  }

  createJob(clientId: string): Observable<Job> {
    this._loggerService.log('create job from network');

    const url = `${this.baseUrl}`;

    return this._authHttpService
      .post(url, { client_id: clientId }, this.headers)
      .pipe(
        map(({ body }: IResponseInfo) => {
          return new Job(body.job_created);
        })
      );
  }

  addEstimate(documentId: string, jobId: string): Observable<JobEstimate> {
    this._loggerService.log('add estimate to job');

    const url = `${this.baseUrl}/${jobId}/estimates`;

    return this._authHttpService
      .post(url, { document_id: documentId }, this.headers)
      .pipe(
        map(({ body }: IResponseInfo) => {
          return new JobEstimate(body.estimate);
        })
      );
  }

  deleteEstimate(documentId: string, jobId: string): Observable<JobEstimate> {
    this._loggerService.log(
      'deleting job estimate: ' + documentId + 'from network'
    );

    const url = `${this.baseUrl}/${jobId}/estimates/${documentId}`;

    return this._authHttpService.delete(url, {}, this.headers).pipe(
      map(({ body }: IResponseInfo) => {
        return new JobEstimate(body.estimates_deleted);
      })
    );
  }

  /**
   * Attachment endpoint to get list attachments doesn't provide url due to performance issue by calling s3client multiple times
   * so to get url must call this endpoint with given attachment id
   */
  fetchAttachment(attachmentId: string, jobId: string) {
    this._loggerService.log(
      'fetch attachment: ' + attachmentId + 'from network'
    );

    const url = `${this.baseUrl}/${jobId}/attachments/${attachmentId}`;

    return this._authHttpService.get(url, {}, this.headers).pipe(
      map(({ body }: IResponseInfo) => {
        return new Attachments(body.attachment);
      })
    );
  }

  deleteAttachment(
    attachmentId: string,
    jobId: string
  ): Observable<Attachments> {
    this._loggerService.log('deleting file: ' + attachmentId + 'from network');

    const url = `${this.baseUrl}/${jobId}/attachments/${attachmentId}`;

    return this._authHttpService.delete(url, {}, this.headers).pipe(
      map(({ body }: IResponseInfo) => {
        return new Attachments(body.attachment);
      })
    );
  }

  saveNote(content: string, jobId: string): Observable<JobNote> {
    this._loggerService.log('save note network');

    const url = `${this.baseUrl}/${jobId}/note`;

    return this._authHttpService.post(url, { content }, this.headers).pipe(
      map(
        ({ body }: IResponseInfo) =>
          new JobNote({
            ...body.note,
            // Set last_updated_date here because backend has an issue with it
            // @see https://invoice2go.atlassian.net/browse/NEW-3156
            last_updated_date: new Date().toISOString(),
          })
      )
    );
  }
}
